import React, { Suspense, lazy, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, ClipboardList, FileText, PenLine, ShieldCheck, Users } from 'lucide-react';
import axios from 'axios';
import api from '../../services/apiService';
import { UserContext } from '../../contexts/UserContext';
import SignaturePadModal from '../contracts/SignaturePadModal';

// pdf.js is heavy and this gate is mounted on every page, so the reader only loads once a
// user actually opens a file.
const MandatoryDocReader = lazy(() => import('./MandatoryDocReader'));

/**
 * App-wide forced "read & sign" gate — the web twin of MandatoryDocGate in windunoapp.
 *
 * Mounted once next to the router, so an unsigned document blocks the whole application
 * rather than a single screen: users used to dodge the mobile block simply by working in
 * the browser. Fed by the same server-authoritative `mandatory-pending` endpoints, which
 * return [] unless the company enabled the matching HSE toggle — so this component is
 * inert (four cheap requests) for everyone who has not opted in, and a stale client can
 * never force a document the company has since turned off.
 *
 * RODO privacy documents ride along as a fourth type, and they are the reason this
 * component also listens on the HTTP layer. RodoConsentMiddleware answers EVERY
 * authenticated web request with 403 + `missing_consents` until the required documents
 * are accepted, but the only place that ever asked for them was the login handler — so a
 * consent added (or a version bumped) mid-session bricked the browser with a wall of 403s
 * and no way to accept anything. The interceptor below turns any such rejection into this
 * sheet, and the consents are pushed to the FRONT of the queue: while they are
 * outstanding every other sign endpoint is being rejected too.
 *
 * Fetch failures are swallowed on purpose: a backend hiccup must never put an
 * undismissable overlay in front of the entire app.
 */

const SEVERITY = {
  critical: { label: 'Critical', color: '#D32F2F' },
  high: { label: 'High', color: '#F97316' },
  medium: { label: 'Medium', color: '#EAB308' },
  low: { label: 'Low', color: '#2563EB' },
  info: { label: 'Info', color: '#6B7280' },
};

const TOOLBOX_SUBJECTS = {
  type_of_operation: 'Type of operation',
  crane_requirements: 'Crane requirements',
  methods_procedures: 'Methods & procedures',
  potential_hazards: 'Potential hazards',
  permit_to_work: 'Permit to work',
  equipment_to_be_used: 'Equipment to be used',
  safety_spill_equipment: 'Safety & spill equipment',
  materials_chemicals: 'Materials & chemicals',
  responsibilities: 'Responsibilities',
  isolations_necessary: 'Isolations necessary',
  access_egress: 'Access & egress',
  impact_adjacent_work: 'Impact on adjacent work',
  tests_monitoring: 'Tests & monitoring',
  environmental_hazards: 'Environmental hazards',
  work_environment: 'Work environment',
  waste_management: 'Waste management',
  general_other_info: 'General / other info',
  flora_fauna_archeological: 'Flora / fauna / archeological',
};

const ENTITIES = {
  '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>',
  '&quot;': '"', '&#39;': "'", '&apos;': "'",
};

/**
 * The rich-text editor stores loose HTML. We render plain text rather than injecting the
 * markup: it keeps the sheet consistent with the mobile one and avoids handing
 * admin-authored HTML straight to dangerouslySetInnerHTML.
 */
const htmlToText = (value) => {
  if (value === null || value === undefined) return '';
  let s = String(value);
  if (!s.trim() || s === 'null' || s === 'undefined') return '';
  s = s
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|h[1-6]|tr|ul|ol)\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ')
    .replace(/<\/\s*li\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  // One pass over the entity map, so a decoded '&' can't be re-decoded by a later rule.
  s = s.replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;|&apos;/g, (m) => ENTITIES[m]);
  return s.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
};

const clean = (value) => {
  const s = value === null || value === undefined ? '' : String(value).trim();
  return s && s !== 'null' && s !== 'undefined' ? s : '';
};

const metaLine = (values) => values.map(clean).filter(Boolean).join('  ·  ');

const section = (label, value) => {
  const text = htmlToText(value);
  return text ? { label, text } : null;
};

const customFieldSections = (j) => {
  if (!Array.isArray(j.custom_fields)) return [];
  return j.custom_fields.map((f) => {
    if (!f || typeof f !== 'object') return null;
    const label = clean(f.label);
    const text = htmlToText(f.value);
    if (!label && !text) return null;
    return { label: label || null, text: text || label };
  }).filter(Boolean);
};

const stringifyList = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((e) => {
    if (e === null || e === undefined) return '';
    if (typeof e === 'object') {
      return clean(e.activity ?? e.name ?? e.text ?? e.value);
    }
    return clean(e);
  }).filter(Boolean);
};

const bulletSection = (label, items) => (
  items.length ? { label, text: items.map((i) => `• ${i}`).join('\n') } : null
);

const discussedSubjects = (j) => {
  const out = Object.entries(TOOLBOX_SUBJECTS)
    .filter(([key]) => j[key] === true)
    .map(([, label]) => label);
  if (Array.isArray(j.custom_subjects)) {
    j.custom_subjects.forEach((s) => {
      if (s && s.checked === true && clean(s.text)) out.push(clean(s.text));
    });
  }
  return out;
};

const absoluteUrl = (url) => {
  const s = clean(url);
  if (!s) return null;
  if (s.startsWith('http')) return s;
  const host = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  return `${host}${s.startsWith('/') ? '' : '/'}${s}`;
};

/** Last path segment of a URL, used when the backend stored no original filename. */
const fileName = (url) => {
  try {
    const path = decodeURIComponent(String(url).split('?')[0].split('#')[0]);
    return path.substring(path.lastIndexOf('/') + 1) || 'Document';
  } catch (_) {
    return 'Document';
  }
};

/**
 * Every file the user has to read before signing.
 *
 * Safety Alerts and Toolbox Talks carry attachments in TWO unrelated places: the legacy
 * single `pdf_document` FileField, and the `attachments` relation — which is where files
 * uploaded through the web editor actually land. Reading only the first one meant a talk
 * with real attached procedures offered nothing to open at all, which is precisely the
 * "read this document" the gate exists to enforce.
 *
 * [primary] names the type's own single-file field; duplicates are collapsed by URL.
 */
const documentList = (j, ...primary) => {
  const out = [];
  const push = (raw, name) => {
    const url = absoluteUrl(raw);
    if (!url || out.some((d) => d.url === url)) return;
    out.push({ url, name: clean(name) || fileName(url) });
  };
  primary.forEach((field) => push(j[field]));
  if (Array.isArray(j.attachments)) {
    j.attachments.forEach((a) => {
      if (a && typeof a === 'object') push(a.file_url || a.file, a.original_filename);
    });
  }
  return out;
};

const fromSafetyAlert = (j) => {
  const sev = SEVERITY[clean(j.severity) || 'medium'] || SEVERITY.medium;
  return {
    key: `safetyAlert-${j.id}`,
    type: 'safetyAlert',
    id: j.id,
    title: clean(j.topic) || 'Safety Alert',
    badgeLabel: sev.label,
    badgeColor: sev.color,
    icon: AlertTriangle,
    requiresSignature: true,
    documents: documentList(j, 'pdf_document'),
    meta: metaLine([j.date, j.category_display ?? j.category, j.project_name]),
    sections: [
      section('Tasks', j.tasks),
      section('HSE comments', j.hse_comments),
      ...customFieldSections(j),
    ].filter(Boolean),
  };
};

const fromToolbox = (j) => ({
  key: `toolbox-${j.id}`,
  type: 'toolbox',
  id: j.id,
  title: clean(j.topic) || 'Toolbox Talk',
  badgeLabel: 'Toolbox Talk',
  badgeColor: '#2563EB',
  icon: Users,
  requiresSignature: true,
  documents: documentList(j, 'pdf_document'),
  meta: metaLine([j.project, j.date, j.time, clean(j.conductor) ? `Conductor: ${clean(j.conductor)}` : '']),
  sections: [
    section('Agenda', j.agenda_items),
    section('Hazards', j.hazards),
    section('Preventive measures', j.preventive_measures),
    bulletSection('Work activities', stringifyList(j.work_activities)),
    bulletSection('Subjects discussed', discussedSubjects(j)),
    ...customFieldSections(j),
    section('Notes', j.notes),
    section('HSE comments', j.hse_comments),
  ].filter(Boolean),
});

const swoLabel = (j) => {
  if (j.project_type === 'installation') return 'CPO';
  if (j.project_type === 'decommissioning') return 'DPO';
  return 'SPO';
};

const fromServiceWorkOrder = (j) => {
  const count = clean(j.number_of_turbines);
  const type = clean(j.turbine_type);
  const turbines = count && type ? `${count} × ${type}` : (count || type);
  const start = clean(j.date_start);
  const end = clean(j.date_end);
  return {
    key: `serviceWorkOrder-${j.id}`,
    type: 'serviceWorkOrder',
    id: j.id,
    title: clean(j.project_name) || clean(j.swo_number) || 'Project Order',
    badgeLabel: swoLabel(j),
    badgeColor: '#0F766E',
    icon: ClipboardList,
    // Not because the endpoint stores an image — it does not — but because the generated
    // PDF draws the signer's stored profile signature. Without one the "Contractor" box
    // comes out blank, so we make sure a signature exists before signing.
    requiresSignature: true,
    // Unsigned orders have no PDF: the file is generated at sign time.
    documents: documentList(j, 'file'),
    meta: metaLine([j.swo_number, j.project_number]),
    sections: [
      start ? { label: 'Period', text: end ? `${start} – ${end}` : start } : null,
      section('Scope of work', j.type_service_work),
      turbines ? { label: 'Turbines', text: turbines } : null,
      section('Required reports', j.required_reports),
      section('Project manager', j.project_manager),
      section('Issued by', j.created_by_name),
    ].filter(Boolean),
  };
};

/**
 * A RODO consent type, from either source: the `check_consents` list or the
 * `missing_consents` array the middleware attaches to its 403 — both carry the same
 * id / name / content / version keys.
 */
const fromRodoConsent = (j) => ({
  key: `rodoConsent-${j.id}`,
  type: 'rodoConsent',
  id: j.id,
  title: clean(j.name) || 'Privacy document',
  badgeLabel: 'Privacy · GDPR',
  badgeColor: '#7C3AED',
  icon: ShieldCheck,
  // Accepting a privacy document is an acknowledgement, not a drawn signature: the
  // endpoint stores no image, so demanding one would block users for nothing.
  requiresSignature: false,
  documents: documentList(j, 'content'),
  meta: metaLine([clean(j.version) ? `Version ${clean(j.version)}` : '']),
  sections: [],
});

const SIGN_URL = {
  safetyAlert: (id) => `/safety-alerts/${id}/sign/`,
  toolbox: (id) => `/toolbox-talks/${id}/sign/`,
  serviceWorkOrder: (id) => `/service-work-orders/${id}/sign/`,
};

/**
 * Attach a rejection handler to one axios client, returning its teardown.
 *
 * Guarded rather than assumed: this component is mounted in front of the whole app, so a
 * client that does not expose an interceptor hub must cost us the 403 shortcut, not the
 * entire UI. The periodic load() still finds the same consents.
 */
const registerRejectionHandler = (client, onRejected) => {
  const hub = client?.interceptors?.response;
  if (!hub || typeof hub.use !== 'function') return null;
  const id = hub.use(undefined, onRejected);
  return () => hub.eject(id);
};

/** Where an acknowledgement goes, and what it carries. */
const signRequest = (doc, signature) => {
  if (doc.type === 'rodoConsent') {
    return { url: '/rodo/consents/sign_consent/', body: { consent_type_id: doc.id } };
  }
  return {
    url: SIGN_URL[doc.type](doc.id),
    body: doc.type === 'serviceWorkOrder' ? {} : { signature: signature || '' },
  };
};

const MandatoryDocGate = () => {
  const { user, updateUserContext } = useContext(UserContext);
  const [docs, setDocs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Files the user has read to the end. Every attached file must reach this list before
  // the sign button unlocks — a document nobody scrolled through is not "read".
  const [readUrls, setReadUrls] = useState([]);
  // The file currently open in the right-hand column, if any.
  const [viewing, setViewing] = useState(null);
  const [showPad, setShowPad] = useState(false);
  // Set once a consent is accepted here, so the queue-empty effect knows this page load
  // spent time behind a 403 wall and its data is missing rather than merely stale.
  const acceptedConsent = useRef(false);

  const current = docs[0] || null;

  const load = useCallback(async () => {
    if (!user?.id) return;
    const get = (url, map) => api.get(url)
      .then((r) => (Array.isArray(r.data) ? r.data.map(map) : []))
      .catch(() => []);
    const [consents, alerts, toolboxes, orders] = await Promise.all([
      get('/rodo/consents/check_consents/', fromRodoConsent),
      get('/safety-alerts/mandatory-pending/', fromSafetyAlert),
      get('/toolbox-talks/mandatory-pending/', fromToolbox),
      get('/service-work-orders/mandatory-pending/', fromServiceWorkOrder),
    ]);
    // Consents first: while one is outstanding the middleware rejects every other sign
    // endpoint, so offering a toolbox to sign would just hand the user a 403.
    setDocs([...consents, ...alerts, ...toolboxes, ...orders]);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  // Any 403 carrying `missing_consents` becomes this sheet. Registered on the shared api
  // client AND on the bare axios default instance, because most screens still import
  // axios directly — a consent that blocks the app must not depend on which client the
  // failing screen happened to use.
  useEffect(() => {
    if (!user?.id) return undefined;
    const onRejected = (error) => {
      const missing = error?.response?.data?.missing_consents;
      if (error?.response?.status === 403 && Array.isArray(missing) && missing.length) {
        // The rejection already names the blocking documents — no second round-trip.
        setDocs((prev) => {
          const known = new Set(prev.map((d) => d.key));
          const fresh = missing.map(fromRodoConsent).filter((d) => !known.has(d.key));
          return fresh.length ? [...fresh, ...prev] : prev;
        });
      }
      return Promise.reject(error);
    };
    const teardowns = [api, axios]
      .map((client) => registerRejectionHandler(client, onRejected))
      .filter(Boolean);
    return () => teardowns.forEach((eject) => eject());
  }, [user?.id]);

  // Everything this page load requested while the consent was outstanding came back 403,
  // so the app behind the overlay is a shell of empty screens. Once the block is gone,
  // start it over rather than leaving the user to guess which parts are missing.
  useEffect(() => {
    if (current || !acceptedConsent.current) return;
    acceptedConsent.current = false;
    try {
      window.location.reload();
    } catch (_) {
      // Reload is a courtesy; a browser that refuses it still has an unblocked app.
    }
  }, [current]);

  // The mobile gate re-checks on app resume; regaining window focus is the browser
  // equivalent. Skipped while a document is already on screen so a refetch can't swap
  // the sheet out from under a half-finished signature.
  useEffect(() => {
    const onFocus = () => { if (!current && !submitting) load(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [current, submitting, load]);

  // Each document starts locked again when it carries files to read.
  useEffect(() => { setReadUrls([]); setViewing(null); setError(''); }, [current?.key]);

  const markRead = useCallback((url) => {
    setReadUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));
  }, []);

  const advance = () => setDocs((prev) => prev.slice(1));

  const submit = useCallback(async (signature) => {
    if (!current) return;
    setSubmitting(true);
    setError('');
    try {
      const { url, body } = signRequest(current, signature);
      await api.post(url, body);
      if (current.type === 'rodoConsent') acceptedConsent.current = true;
      advance();
    } catch (e) {
      const status = e?.response?.status;
      const detail = String(e?.response?.data?.error || e?.response?.data?.detail || '').toLowerCase();
      // This overlay cannot be dismissed, so a permanent rejection would lock the user out
      // of the whole app. The two rejections that mean "the reason to block is already
      // gone" — signed elsewhere in the meantime, or the document was removed — move on.
      if (status === 404 || (status === 400 && detail.includes('already signed'))) {
        advance();
      } else {
        setError(e?.response?.data?.error || 'Could not record your signature. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [current]);

  const onSignClick = () => {
    if (current?.requiresSignature && !user?.user_signature) {
      setShowPad(true);
      return;
    }
    submit(user?.user_signature);
  };

  // A signature drawn here is stored on the profile first: the Project Order PDF renders
  // it from there, and it saves every future document the same detour.
  const onPadSign = async ({ signatureImage, useStored }) => {
    const signature = useStored ? (user?.user_signature || '') : signatureImage;
    if (!useStored && signatureImage && user?.id) {
      try {
        await api.patch(`/users/${user.id}/`, { user_signature: signatureImage });
        await updateUserContext?.();
      } catch (_) {
        // For a Project Order the profile is the ONLY place the drawing can live — the
        // sign endpoint stores no image and the PDF renders it from there. Signing anyway
        // would produce a legally signed document with an empty contractor box, so stop
        // and let the user retry. Other types carry the image in the sign request itself,
        // where a failed profile write costs nothing but the convenience of reuse.
        if (current?.type === 'serviceWorkOrder') {
          setShowPad(false);
          setError('Could not save your signature to your profile, and a Project Order '
            + 'takes its signature from there. Please try again.');
          return;
        }
      }
    }
    setShowPad(false);
    await submit(signature);
  };

  if (!current) return null;

  const Icon = current.icon;
  // `every` on an empty list is true, so a document with no attachments is never locked.
  const locked = !current.documents.every((d) => readUrls.includes(d.url));
  const isConsent = current.type === 'rodoConsent';
  // Say this up front rather than springing a drawing pad on the user when they finally
  // click sign — and, for Project Orders, because the generated PDF takes the signature
  // from the profile, so an empty profile means an empty contractor box.
  const needsNewSignature = Boolean(current.requiresSignature) && !user?.user_signature;

  return (
    <>
      <div
        data-mandatory-gate={current.type}
        // Above every dialog (see the ladder in components/ui/dialog.js). This
        // gate blocks the app until the document is signed, so a modal left open
        // underneath must not be able to cover it.
        className="fixed inset-0 z-[100030] bg-black/70 flex items-center justify-center p-4"
      >
        <div className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] flex flex-col ${
          viewing ? 'max-w-6xl' : 'max-w-2xl'
        }`}>
          <div
            className="px-6 py-5 rounded-t-2xl text-white flex items-start gap-3"
            style={{ backgroundColor: current.badgeColor }}
          >
            <Icon className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-wider opacity-90">
                {current.badgeLabel}
              </p>
              <h2 className="text-lg font-bold leading-snug break-words">{current.title}</h2>
              {current.meta && <p className="text-xs opacity-90 mt-1">{current.meta}</p>}
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <div className={`px-6 py-5 overflow-y-auto ${
            viewing ? 'md:w-2/5 md:border-r border-slate-100 flex-shrink-0' : 'w-full flex-1'
          }`}>
            <p className="text-sm text-slate-500 mb-4">
              {isConsent
                ? 'You must read and accept this document before you can continue using the application.'
                : 'You must read and sign this document before you can continue working.'}
            </p>

            {needsNewSignature && (
              <div
                data-no-signature
                className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200"
              >
                <PenLine className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  You have no signature saved yet. You will be asked to draw one before this
                  document can be signed — it is stored on your profile and reused for every
                  document afterwards.
                </p>
              </div>
            )}

            {current.sections.map((s, i) => (
              <div key={`${current.key}-${i}`} className="mb-4">
                {s.label && (
                  <p className="text-[11px] font-black uppercase tracking-wide text-slate-400 mb-1">
                    {s.label}
                  </p>
                )}
                <p className="text-sm text-slate-700 whitespace-pre-line break-words">{s.text}</p>
              </div>
            ))}

            {current.documents.length > 0 && (
              <div className="mt-2">
                <p className="text-[11px] font-black uppercase tracking-wide text-slate-400 mb-2">
                  {current.documents.length > 1
                    ? `Documents to read (${current.documents.length})`
                    : 'Document to read'}
                </p>
                <div className="flex flex-col gap-2">
                  {current.documents.map((d) => {
                    const isRead = readUrls.includes(d.url);
                    const isOpen = viewing?.url === d.url;
                    return (
                      <button
                        key={d.url}
                        type="button"
                        data-doc-item
                        onClick={() => setViewing(d)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold text-left ${
                          isRead
                            ? 'border-green-300 bg-green-50 text-green-800'
                            : isOpen
                              ? 'border-slate-400 bg-slate-50 text-slate-800'
                              : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {isRead
                          ? <Check className="w-4 h-4 flex-shrink-0" />
                          : <FileText className="w-4 h-4 flex-shrink-0" />}
                        <span className="truncate">{d.name}</span>
                        {!isRead && isOpen && (
                          <span className="ml-auto text-[11px] font-normal text-slate-500 flex-shrink-0">
                            scroll to the end
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <p className="text-sm text-red-600 mt-4">{error}</p>}
          </div>

          {viewing && (
            <div
              data-doc-column
              className="flex-1 min-h-0 border-t md:border-t-0 border-slate-100 flex flex-col h-[45vh] md:h-auto"
            >
              <Suspense
                fallback={(
                  <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
                    Loading viewer…
                  </div>
                )}
              >
                <MandatoryDocReader doc={viewing} onRead={() => markRead(viewing.url)} />
              </Suspense>
            </div>
          )}
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-400">
              {docs.length > 1 ? `${docs.length} documents waiting` : 'Last document'}
              {locked
                ? (current.documents.length > 1
                  ? ' · read every document to the end'
                  : ' · read the document to the end')
                : ''}
            </p>
            <button
              type="button"
              onClick={onSignClick}
              disabled={submitting || locked}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: current.badgeColor }}
            >
              {submitting
                ? (isConsent ? 'Accepting…' : 'Signing…')
                : (isConsent
                  ? 'I have read this — accept'
                  : (needsNewSignature ? 'Add signature & sign' : 'I have read this — sign'))}
            </button>
          </div>
        </div>
      </div>

      {showPad && (
        <SignaturePadModal
          title="Your signature"
          allowStored={Boolean(user?.user_signature)}
          // Above this gate's own overlay. Without it the pad mounts behind the
          // full-screen backdrop: "Add signature & sign" appeared to do nothing,
          // which locked out every user with no stored signature — the exact
          // people the button exists for.
          overlayZClass="z-[100040]"
          onClose={() => setShowPad(false)}
          onSign={onPadSign}
        />
      )}
    </>
  );
};

export default MandatoryDocGate;
